// src/pages/portal/ActAsClientContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

function decodeMaybeTwice(v) {
  if (!v) return v;
  try {
    const once = decodeURIComponent(v);
    // If it still looks encoded (contains %xx), decode one more time.
    return /%[0-9A-Fa-f]{2}/.test(once) ? decodeURIComponent(once) : once;
  } catch {
    return v;
  }
}

const STORAGE_KEY = "portal_act_as_ctx";

const ActAsClientContext = createContext({
  clientId: null,
  orgId: null,
  actAsName: null,
  isActingAs: false,
  getActAsHeaders: () => ({}),
  stopActingAs: () => {},
  startActingAs: (_ctx) => {},
});

export const useActAsClient = () => useContext(ActAsClientContext);

export function ActAsClientProvider({ children }) {
  const [params] = useSearchParams();

  const [state, setState] = useState(() => {
    // 1) Try URL first (trust on initial mount)
    const fromUrl = {
      clientId:  decodeMaybeTwice(params.get("actAsClient") || params.get("as") || null),
      orgId:     decodeMaybeTwice(params.get("actAsOrg") || null),
      actAsName: decodeMaybeTwice(params.get("actAsName") || null),
    };

    if (fromUrl.clientId) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }

    // 2) Else try sessionStorage (persist across internal nav)
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return {
          clientId: parsed.clientId || null,
          orgId: parsed.orgId || null,
          actAsName: parsed.actAsName || null,
        };
      } catch {
        /* ignore parse errors */
      }
    }

    // 3) Default (not acting-as)
    return { clientId: null, orgId: null, actAsName: null };
  });

  // Keep sessionStorage in sync with state
  useEffect(() => {
    if (state?.clientId) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  const value = useMemo(() => {
    const isActingAs = Boolean(state.clientId);

    const getActAsHeaders = () => {
      if (!isActingAs) return {};
      const h = { "X-Act-As-Client": state.clientId };
      if (state.orgId) h["X-Act-As-Org"] = state.orgId;
      if (state.actAsName) h["X-Act-As-Name"] = state.actAsName;
      return h;
    };

    const stopActingAs = () => {
      sessionStorage.removeItem(STORAGE_KEY);
      setState({ clientId: null, orgId: null, actAsName: null });
    };

    const startActingAs = (ctx) => {
      const next = {
        clientId:  ctx?.clientId || ctx?.actAsClient || null,
        orgId:     ctx?.orgId || ctx?.actAsOrg || null,
        actAsName: ctx?.actAsName || null,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setState(next);
    };

    return {
      clientId: state.clientId,
      orgId: state.orgId,
      actAsName: state.actAsName,
      isActingAs,
      getActAsHeaders,
      stopActingAs,
      startActingAs,
    };
  }, [state]);

  return (
    <ActAsClientContext.Provider value={value}>
      {children}
    </ActAsClientContext.Provider>
  );
}